import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const STYLES = {
    title: { font: { bold: true, size: 18, color: { argb: "FF000080" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFDDEBF7" } }, alignment: { horizontal: "center", vertical: "middle" } },
    dateLine: { font: { italic: true, size: 11, color: { argb: "FF555555" } }, alignment: { horizontal: "left", vertical: "middle" } },
    header: { font: { bold: true, color: { argb: "FFFFFFFF" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } }, alignment: { vertical: "middle", horizontal: "center" } },
    data: { font: { bold: false }, alignment: { vertical: "middle", horizontal: "center" } },
    branchGroup: { font: { bold: true, size: 12, color: { argb: "FFFFFFFF" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E75B6" } }, alignment: { vertical: "middle", horizontal: "left" } },
    subtotal: { font: { bold: true, color: { argb: "FF1F4E79" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2EFDA" } }, alignment: { vertical: "middle", horizontal: "center" } },
    grandTotal: { font: { bold: true, size: 12, color: { argb: "FFFFFFFF" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF375623" } }, alignment: { vertical: "middle", horizontal: "center" } },
    zebra: { fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } } },
};

const border = {
    top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" },
};

function applyStyle(cell, style) {
    if (style.font) cell.font = { ...cell.font, ...style.font };
    if (style.fill) cell.fill = style.fill;
    if (style.alignment) cell.alignment = style.alignment;
}

function addCell(worksheet, row, col, value, style) {
    const cell = worksheet.getCell(row, col);
    cell.value = value;
    cell.border = border;
    applyStyle(cell, style);
}

function formatDateTime(d) {
    if (!d) return "";
    const date = new Date(d);
    return isNaN(date.getTime()) ? "" : date.toLocaleString("en-GB");
}

async function generateUserActivityLogExcel(apiData) {
    if (!apiData || apiData.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("User Activity Log");

    const columns = [
        { header: "No.", width: 8 },
        { header: "Full Name", width: 24 },
        { header: "NIC No", width: 18 },
        { header: "Date/Time", width: 22 },
        { header: "Activity", width: 30 },
        { header: "Status", width: 16 },
        { header: "Designation", width: 20 },
        { header: "Branch", width: 22 },
    ];

    const colsCount = columns.length;
    columns.forEach((c, i) => { ws.getColumn(i + 1).width = c.width; });

    ws.mergeCells(1, 1, 1, colsCount);
    const titleCell = ws.getCell("A1");
    titleCell.value = "User Activity Log Report";
    applyStyle(titleCell, STYLES.title);
    ws.getRow(1).height = 50;

    ws.mergeCells(2, 1, 2, colsCount);
    const dateCell = ws.getCell("A2");
    dateCell.value = `Printed Date: ${new Date().toLocaleDateString("en-GB")}`;
    applyStyle(dateCell, STYLES.dateLine);

    ws.addRow([]);

    const headerRow = ws.addRow(columns.map(c => c.header));
    headerRow.eachCell((cell) => { applyStyle(cell, STYLES.header); cell.border = border; });
    headerRow.height = 22;

    const sorted = [...apiData].sort((a, b) => {
        const un = (a.fullName || "").localeCompare(b.fullName || "");
        if (un !== 0) return un;
        return (a.branchName || "").localeCompare(b.branchName || "");
    });

    let currentRow = 4;
    let grandTotal = 0;
    let prevUser = "";
    let userCount = 0;

    sorted.forEach((item, idx) => {
        const user = item.fullName || "Unknown";

        if (idx > 0 && user !== prevUser) {
            ws.mergeCells(currentRow, 1, currentRow, colsCount);
            addCell(ws, currentRow, 1, `User Total: ${prevUser}  (${userCount} records)`, STYLES.subtotal);
            for (let c = 2; c <= colsCount; c++) addCell(ws, currentRow, c, "", STYLES.subtotal);
            currentRow++;
            userCount = 0;
        }

        if (user !== prevUser) {
            ws.mergeCells(currentRow, 1, currentRow, colsCount);
            addCell(ws, currentRow, 1, `User: ${user}`, STYLES.branchGroup);
            for (let c = 2; c <= colsCount; c++) addCell(ws, currentRow, c, "", STYLES.branchGroup);
            currentRow++;
            prevUser = user;
        }

        const vals = [
            idx + 1,
            item.fullName || "",
            item.nic || "",
            formatDateTime(item.logTime),
            item.activityName || "",
            item.state || "",
            item.designation || "",
            item.branchName || "",
        ];

        const dataRow = ws.addRow(vals);
        dataRow.eachCell((cell, colNum) => {
            cell.border = border;
            applyStyle(cell, STYLES.data);
        });
        if (idx % 2 === 1) {
            dataRow.eachCell((cell) => { if (!cell.fill || !cell.fill.fgColor) cell.fill = STYLES.zebra.fill; });
        }
        dataRow.height = 20;

        userCount++;
        grandTotal++;
        currentRow++;
    });

    if (userCount > 0) {
        ws.mergeCells(currentRow, 1, currentRow, colsCount);
        addCell(ws, currentRow, 1, `User Total: ${prevUser}  (${userCount} records)`, STYLES.subtotal);
        for (let c = 2; c <= colsCount; c++) addCell(ws, currentRow, c, "", STYLES.subtotal);
        currentRow++;
    }

    ws.mergeCells(currentRow, 1, currentRow, colsCount);
    addCell(ws, currentRow, 1, `GRAND TOTAL  (${grandTotal} records)`, STYLES.grandTotal);
    for (let c = 2; c <= colsCount; c++) addCell(ws, currentRow, c, "", STYLES.grandTotal);

    ws.views = [{ state: "frozen", xSplit: 0, ySplit: 3 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const fileName = `UserActivityLog_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.xlsx`;
    saveAs(new Blob([buffer]), fileName);
}

export default generateUserActivityLogExcel;
